/**
 * Folder Management Store
 *
 * Manages state for folder CRUD operations and folder tree structure.
 */

import { create } from 'zustand';
import {
  listFolders,
  createFolder,
  updateFolder,
  deleteFolder,
  getFolderFilesCount,
  parseFolderPath,
  isChildOf,
  type Folder,
  type CreateFolderParams,
  type UpdateFolderParams,
  type FolderFilesCount,
} from '../api/folders';
import { parseApiError, showErrorNotification } from '../utils/errorHandling';
import toast from 'react-hot-toast';

export interface FolderNode {
  folder: Folder;
  children: FolderNode[];
  depth: number;
  expanded: boolean;
}

interface FolderState {
  // Folder list state
  folders: Folder[];
  folderTree: FolderNode[];
  selectedFolder: Folder | null;
  selectedFolderFilesCount: number | null;
  total: number;

  // UI state
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;

  // Dialog state
  isCreateDialogOpen: boolean;
  isEditDialogOpen: boolean;
  isDeleteDialogOpen: boolean;
  isWarningDialogOpen: boolean;

  // Actions
  loadFolders: () => Promise<void>;
  selectFolder: (folder: Folder | null) => void;
  createNewFolder: (params: CreateFolderParams) => Promise<Folder | null>;
  updateSelectedFolder: (
    params: UpdateFolderParams,
    applyToContents: boolean
  ) => Promise<void>;
  deleteSelectedFolder: () => Promise<void>;
  loadFolderFilesCount: (folderId: string) => Promise<void>;

  // Tree management
  toggleExpanded: (folderId: string) => void;
  expandAll: () => void;
  collapseAll: () => void;

  // Dialog management
  openCreateDialog: () => void;
  closeCreateDialog: () => void;
  openEditDialog: (folder: Folder) => void;
  closeEditDialog: () => void;
  openDeleteDialog: (folder: Folder) => void;
  closeDeleteDialog: () => void;
  openWarningDialog: () => void;
  closeWarningDialog: () => void;

  resetState: () => void;
}

const initialState = {
  folders: [],
  folderTree: [],
  selectedFolder: null,
  selectedFolderFilesCount: null,
  total: 0,
  isLoading: false,
  isSaving: false,
  error: null,
  isCreateDialogOpen: false,
  isEditDialogOpen: false,
  isDeleteDialogOpen: false,
  isWarningDialogOpen: false,
};

/**
 * Build folder tree from flat list of folders
 */
const buildFolderTree = (folders: Folder[], expandedIds: Set<string>): FolderNode[] => {
  const sorted = [...folders].sort((a, b) => a.folder_path.localeCompare(b.folder_path));
  const tree: FolderNode[] = [];
  const nodeMap = new Map<string, FolderNode>();

  // Create nodes for all folders
  sorted.forEach(folder => {
    const parts = parseFolderPath(folder.folder_path);
    const node: FolderNode = {
      folder,
      children: [],
      depth: parts.length,
      expanded: expandedIds.has(folder.id),
    };
    nodeMap.set(folder.folder_path, node);
  });

  // Build parent-child relationships
  sorted.forEach(folder => {
    const node = nodeMap.get(folder.folder_path)!;

    // Find parent
    let parentNode: FolderNode | null = null;
    for (const [path, candidate] of nodeMap) {
      if (path !== folder.folder_path && isChildOf(folder.folder_path, path)) {
        // Check if this is the most immediate parent
        if (!parentNode || candidate.depth > parentNode.depth) {
          parentNode = candidate;
        }
      }
    }

    if (parentNode) {
      parentNode.children.push(node);
    } else {
      tree.push(node);
    }
  });

  return tree;
};

export const useFolderStore = create<FolderState>((set, get) => ({
  ...initialState,

  /**
   * Load all folders from API
   */
  loadFolders: async () => {
    set({ isLoading: true, error: null });

    try {
      const response = await listFolders();
      const expandedIds = new Set<string>();

      // Preserve expanded state
      get().folderTree.forEach(node => {
        const collectExpanded = (n: FolderNode) => {
          if (n.expanded) expandedIds.add(n.folder.id);
          n.children.forEach(collectExpanded);
        };
        collectExpanded(node);
      });

      const tree = buildFolderTree(response.folders, expandedIds);

      set({
        folders: response.folders,
        folderTree: tree,
        isLoading: false,
      });
    } catch (error) {
      const errorMessage = parseApiError(error);
      set({ error: errorMessage, isLoading: false });
      showErrorNotification('Failed to load folders', errorMessage);
    }
  },

  /**
   * Select a folder
   */
  selectFolder: (folder: Folder | null) => {
    set({ selectedFolder: folder, selectedFolderFilesCount: null });
  },

  /**
   * Create a new folder
   */
  createNewFolder: async (params: CreateFolderParams): Promise<Folder | null> => {
    set({ isSaving: true, error: null });

    try {
      const folder = await createFolder(params);
      toast.success(`Folder "${folder.folder_path}" created successfully`);

      // Reload folders to update tree
      await get().loadFolders();

      set({ isSaving: false, isCreateDialogOpen: false });
      return folder;
    } catch (error) {
      const errorMessage = parseApiError(error);
      set({ error: errorMessage, isSaving: false });
      showErrorNotification('Failed to create folder', errorMessage);
      return null;
    }
  },

  /**
   * Update selected folder
   */
  updateSelectedFolder: async (
    params: UpdateFolderParams,
    applyToContents: boolean = false
  ) => {
    const { selectedFolder } = get();
    if (!selectedFolder) return;

    set({ isSaving: true, error: null });

    try {
      const updated = await updateFolder(selectedFolder.id, params, applyToContents);

      toast.success(
        applyToContents
          ? `Folder "${updated.folder_path}" updated and changes applied to contents`
          : `Folder "${updated.folder_path}" updated`
      );

      // Reload folders to update tree
      await get().loadFolders();

      set({
        selectedFolder: updated,
        isSaving: false,
        isEditDialogOpen: false,
        isWarningDialogOpen: false
      });
    } catch (error) {
      const errorMessage = parseApiError(error);
      set({ error: errorMessage, isSaving: false });
      showErrorNotification('Failed to update folder', errorMessage);
    }
  },

  /**
   * Delete selected folder
   */
  deleteSelectedFolder: async () => {
    const { selectedFolder } = get();
    if (!selectedFolder) return;

    set({ isSaving: true, error: null });

    try {
      await deleteFolder(selectedFolder.id);
      toast.success(`Folder "${selectedFolder.folder_path}" deleted`);

      // Reload folders to update tree
      await get().loadFolders();

      set({
        selectedFolder: null,
        isSaving: false,
        isDeleteDialogOpen: false,
      });
    } catch (error) {
      const errorMessage = parseApiError(error);
      set({ error: errorMessage, isSaving: false });
      showErrorNotification('Failed to delete folder', errorMessage);
    }
  },

  /**
   * Load file count for a folder (for warning dialog)
   */
  loadFolderFilesCount: async (folderId: string) => {
    try {
      const result = await getFolderFilesCount(folderId);
      set({ selectedFolderFilesCount: result.files_count });
    } catch (error) {
      const errorMessage = parseApiError(error);
      showErrorNotification('Failed to get file count', errorMessage);
    }
  },

  /**
   * Toggle folder expanded state in tree
   */
  toggleExpanded: (folderId: string) => {
    const toggleNode = (nodes: FolderNode[]): FolderNode[] => {
      return nodes.map(node => {
        if (node.folder.id === folderId) {
          return { ...node, expanded: !node.expanded };
        }
        if (node.children.length > 0) {
          return { ...node, children: toggleNode(node.children) };
        }
        return node;
      });
    };

    set({ folderTree: toggleNode(get().folderTree) });
  },

  /**
   * Expand all folders in tree
   */
  expandAll: () => {
    const expandNode = (nodes: FolderNode[]): FolderNode[] => {
      return nodes.map(node => ({
        ...node,
        expanded: true,
        children: expandNode(node.children),
      }));
    };

    set({ folderTree: expandNode(get().folderTree) });
  },

  /**
   * Collapse all folders in tree
   */
  collapseAll: () => {
    const collapseNode = (nodes: FolderNode[]): FolderNode[] => {
      return nodes.map(node => ({
        ...node,
        expanded: false,
        children: collapseNode(node.children),
      }));
    };

    set({ folderTree: collapseNode(get().folderTree) });
  },

  // Dialog management
  openCreateDialog: () => set({ isCreateDialogOpen: true }),
  closeCreateDialog: () => set({ isCreateDialogOpen: false, error: null }),

  openEditDialog: (folder: Folder) => set({
    selectedFolder: folder,
    isEditDialogOpen: true
  }),
  closeEditDialog: () => set({ isEditDialogOpen: false, error: null }),

  openDeleteDialog: (folder: Folder) => set({
    selectedFolder: folder,
    isDeleteDialogOpen: true
  }),
  closeDeleteDialog: () => set({ isDeleteDialogOpen: false }),

  openWarningDialog: () => set({ isWarningDialogOpen: true }),
  closeWarningDialog: () => set({ isWarningDialogOpen: false }),

  /**
   * Reset state to initial values
   */
  resetState: () => set(initialState),
}));
